// apps/codeframe/src/chrome/timeline/NodePicker.tsx
import { useMemo } from 'react';
import { useCanvasStore } from '../../store/canvas-store';
import { hoverHighlight, clearHoverHighlight } from './useTrackHighlight';
import { nodeLabel } from './pickOnCanvas';

interface NodeOpt {
  cfId: string;
  label: string;
}

/** CSS.escape with a raw fallback (jsdom lacks the CSS global). */
function esc(v: string): string {
  return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(v) : v;
}

/** Enumerate animatable nodes (data-cf-id) inside the frame's live DOM root. */
function collectNodes(frameId: string): NodeOpt[] {
  if (typeof document === 'undefined') return [];
  const root = document.querySelector(`[data-frame-id="${esc(frameId)}"]`);
  if (!root) return [];
  const out: NodeOpt[] = [];
  root.querySelectorAll<HTMLElement>('[data-cf-id]').forEach((el) => {
    const cfId = el.getAttribute('data-cf-id');
    if (!cfId) return;
    out.push({ cfId, label: nodeLabel(el) });
  });
  return out;
}

export function NodePicker({
  frameId,
  onPick,
  onClose,
}: {
  frameId: string;
  onPick: (cfId: string, label: string) => void;
  onClose: () => void;
}) {
  const layerSel = useCanvasStore((s) => s.layerSelection);
  const nodes = useMemo(() => collectNodes(frameId), [frameId]);

  return (
    <div className="absolute left-2 bottom-2 z-10 max-h-48 w-64 overflow-auto rounded-md border border-[var(--color-chip-border)] bg-[var(--color-surface)] text-xs font-[family-name:var(--font-sans)] shadow-[var(--shadow-2)]">
      <div className="flex items-center justify-between border-b border-[var(--color-chip-border)] px-2 py-1 text-[var(--color-text-muted)]">
        <span>Pick an element</span>
        <button onClick={onClose} className="hover:text-[var(--color-accent)]">
          esc
        </button>
      </div>
      {nodes.length === 0 && <div className="px-2 py-2 text-[var(--color-text-muted)]">No elements found.</div>}
      {nodes.map((n) => (
        <button
          key={n.cfId}
          onMouseEnter={() => hoverHighlight(frameId, n.cfId)}
          onMouseLeave={clearHoverHighlight}
          onClick={() => onPick(n.cfId, n.label)}
          className={`block w-full truncate px-2 py-1 text-left hover:bg-[var(--color-menu-hover)] ${
            layerSel?.cfNodeId === n.cfId ? 'text-[var(--color-accent)]' : ''
          }`}
        >
          {n.label}
        </button>
      ))}
    </div>
  );
}
